import { useCallback, useEffect, useMemo, useState } from 'react';

const isStandaloneDisplay = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIosDevice = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const isSafariBrowser = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('crios') && !ua.includes('fxios') && !ua.includes('edgios');
};

const isSupportedInstallPlatform = () =>
  typeof window !== 'undefined' && ('BeforeInstallPromptEvent' in window || /android|iphone|ipad|ipod/i.test(window.navigator.userAgent));

export const usePwaInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => isStandaloneDisplay());
  const [isInstallReady, setIsInstallReady] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setIsInstallReady(true);
    };

    const onAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallReady(false);
      setDeferredPrompt(null);
      setIsInstalling(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const canInstall = useMemo(
    () => !isInstalled && isSupportedInstallPlatform() && (isInstallReady || isIosDevice()),
    [isInstallReady, isInstalled]
  );

  const showIosInstructions = useMemo(
    () => !isInstalled && isIosDevice() && isSafariBrowser() && !isInstallReady,
    [isInstalled, isInstallReady]
  );

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      // iOS Safari does not support beforeinstallprompt; users install via
      // Share -> Add to Home Screen. Keep button interactive for guidance.
      if (isIosDevice() && isSafariBrowser() && !isStandaloneDisplay()) {
        return 'ios-manual';
      }
      return false;
    }
    setIsInstalling(true);
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setIsInstallReady(false);
    setIsInstalling(false);
    return choice?.outcome === 'accepted';
  }, [deferredPrompt]);

  return { canInstall, isInstalled, isInstalling, promptInstall, showIosInstructions };
};

